import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { HEAD_DOCTOR_COPY } from '../headDoctorCopy';
import { HEAD_DOCTOR_PATHS } from '../../doctor/doctorPaths';
import { useClinicalReports } from '../../clinical-reports/ClinicalReportsContext';
import { REST_DAY_STATUS_LABELS, formatMonthKey } from './reportUtils';
import ReportSectionHead from './ReportSectionHead';
import ReportsRestPeopleInsight from './ReportsRestPeopleInsight';
import StatisticalDataTable, { statNumberColumn, statPercentColumn, statRankColumn } from './StatisticalDataTable';
import { HeadDoctorStatCard } from '../ui';
import Icon from '../../common/Icons';
import {
  ClinicalBarChart,
  ClinicalDoughnutChart,
  ClinicalLineChart,
  ReportChartCard,
  ReportsChartCol,
  ReportsChartGrid
} from './charts/ClinicalCharts';
import { CHART_PALETTE, REST_DAY_STATUS_COLORS } from './charts/chartTheme';
import {
  computeShares,
  doughnutChartData,
  labeledPieData,
  lineChartData
} from './charts/chartDataHelpers';

const COPY = HEAD_DOCTOR_COPY.REPORTS;

const DURATION_LABEL_KEYS = {
  '1-3': 'DURATION_BUCKET_1_3',
  '4-7': 'DURATION_BUCKET_4_7',
  '8-14': 'DURATION_BUCKET_8_14',
  '15+': 'DURATION_BUCKET_15_PLUS'
};

function durationBucketLabel(key) {
  const labelKey = DURATION_LABEL_KEYS[key];
  return labelKey ? COPY[labelKey] : key;
}

export default function HeadDoctorReportsReposPage() {
  const { data, summary, loading, reportPaths } = useClinicalReports();
  const restListPath =
    reportPaths && Object.prototype.hasOwnProperty.call(reportPaths, 'JOURS_REPOS')
      ? reportPaths.JOURS_REPOS
      : reportPaths
        ? null
        : HEAD_DOCTOR_PATHS.JOURS_REPOS;
  const byStatus = data?.rest_days?.by_status || [];
  const byMonth = data?.rest_days?.by_month || [];
  const buckets = data?.rest_days?.duration_buckets || [];
  const doctors = useMemo(
    () => computeShares(data?.rest_days?.by_doctor || [], 'total_rest_days'),
    [data]
  );

  const statusPie = useMemo(
    () =>
      labeledPieData(byStatus, {
        labelKey: 'status',
        labelMap: REST_DAY_STATUS_LABELS,
        colorMap: REST_DAY_STATUS_COLORS
      }),
    [byStatus]
  );

  const trendData = useMemo(
    () =>
      lineChartData(
        byMonth.map((row) => formatMonthKey(row.month_key)),
        [
          {
            label: COPY.CHART_REST_CERTIFICATES,
            data: byMonth.map((row) => Number(row.count)),
            color: CHART_PALETTE[0],
            fill: true,
            yAxisID: 'y'
          },
          {
            label: COPY.CHART_REST_UNIQUE_MEMBERS,
            data: byMonth.map((row) => Number(row.unique_persons ?? row.unique_members ?? 0)),
            color: CHART_PALETTE[4],
            fill: false,
            yAxisID: 'y'
          },
          {
            label: COPY.CHART_REST_DAYS_TOTAL,
            data: byMonth.map((row) => Number(row.total_days)),
            color: CHART_PALETTE[2],
            fill: false,
            yAxisID: 'y1'
          }
        ]
      ),
    [byMonth]
  );

  const durationBar = useMemo(
    () => ({
      labels: buckets.map((b) => durationBucketLabel(b.bucket_key)),
      datasets: [
        {
          label: COPY.CHART_REST_CERTIFICATES,
          data: buckets.map((b) => Number(b.count)),
          backgroundColor: CHART_PALETTE[1],
          borderRadius: 6,
          maxBarThickness: 44
        }
      ]
    }),
    [buckets]
  );

  const doctorsBar = useMemo(
    () => ({
      labels: doctors.map((d) => d.doctor_name),
      datasets: [
        {
          label: COPY.CHART_REST_DAYS_TOTAL,
          data: doctors.map((d) => Number(d.total_rest_days)),
          backgroundColor: CHART_PALETTE[0],
          borderRadius: 6,
          maxBarThickness: 40
        }
      ]
    }),
    [doctors]
  );

  const tableColumns = [
    { key: 'rank', label: '#', className: 'text-muted', render: (_r, i) => i + 1 },
    { key: 'doctor_name', label: COPY.COL_DOCTOR, className: 'fw-semibold', render: (r) => r.doctor_name },
    statRankColumn(COPY.COL_REST_DAYS, 'total_rest_days'),
    statNumberColumn(COPY.COL_REST_CERTIFICATES, 'certificate_count'),
    statPercentColumn(COPY.COL_SHARE)
  ];

  const hasAnyRecords =
    summary.rest_day_certificates > 0 ||
    summary.rest_day_cancelled > 0 ||
    byStatus.some((row) => Number(row.count) > 0);
  const hasIssuedData = summary.rest_day_certificates > 0;
  const avgDays = Number(summary.rest_day_average_days || 0);

  return (
    <>
      <ReportSectionHead
        badge={COPY.SUB_REPOS}
        title={COPY.PAGE_REPOS_TITLE}
        description={COPY.PAGE_REPOS_DESC}
        actions={
          restListPath ? (
            <Link to={restListPath} className="hd-reports-action-link">
              <Icon name="calendar" size={16} aria-hidden />
              <span>
                {COPY.PAGE_REPOS_VIEW_LIST}
                <small className="d-block hd-reports-action-link__hint">{COPY.PAGE_REPOS_VIEW_LIST_HINT}</small>
              </span>
              <Icon name="chevronRight" size={14} className="hd-reports-action-link__chevron" aria-hidden />
            </Link>
          ) : null
        }
      />

      <ReportsRestPeopleInsight summary={summary} loading={loading} className="mb-3" />

      <div className="hd-reports-kpis hd-reports-kpis--5 mb-4">
        <HeadDoctorStatCard
          title={COPY.KPI_REST_CERTIFICATES}
          value={summary.rest_day_certificates}
          icon="calendar"
          tone="indigo"
          loading={loading}
        />
        <HeadDoctorStatCard
          title={COPY.KPI_REST_CANCELLED}
          value={summary.rest_day_cancelled}
          icon="times"
          tone="slate"
          loading={loading}
        />
        <HeadDoctorStatCard
          title={COPY.KPI_REST_DAYS}
          value={summary.rest_day_total_days}
          icon="pause"
          tone="amber"
          loading={loading}
        />
        <HeadDoctorStatCard
          title={COPY.KPI_REST_IN_PROGRESS}
          value={summary.rest_day_in_progress}
          icon="infoCircle"
          tone="teal"
          loading={loading}
          subtitle={COPY.KPI_REST_IN_PROGRESS_HINT}
        />
        <HeadDoctorStatCard
          title={COPY.KPI_REST_AVG}
          value={avgDays}
          icon="chartBar"
          tone="indigo"
          loading={loading}
          decimals={1}
          subtitle={COPY.KPI_REST_AVG_HINT}
        />
      </div>

      {loading ? (
        <div className="hd-reports-panel mb-4">
          <div className="hd-reports-loading">
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
            <span>{COPY.LOADING}</span>
          </div>
        </div>
      ) : !hasAnyRecords ? (
        <div className="hd-reports-panel mb-4">
          <p className="hd-reports-empty mb-0">{COPY.EMPTY_REST_DAYS}</p>
        </div>
      ) : (
        <>
          {byStatus.length > 0 && (
            <ReportsChartGrid>
              <ReportsChartCol lg={hasIssuedData ? 4 : 12}>
                <ReportChartCard
                  title={COPY.CHART_REST_STATUS_TITLE}
                  subtitle={COPY.CHART_REST_STATUS_DESC}
                  height={280}
                >
                  <ClinicalDoughnutChart
                    data={doughnutChartData(statusPie.labels, statusPie.values, statusPie.colors)}
                  />
                </ReportChartCard>
              </ReportsChartCol>
              {hasIssuedData ? (
                <ReportsChartCol lg={8}>
                  <ReportChartCard
                    title={COPY.CHART_REST_TREND_TITLE}
                    subtitle={COPY.CHART_REST_TREND_DESC}
                    height={280}
                  >
                    <ClinicalLineChart
                      data={trendData}
                      xLabel={COPY.CHART_AXIS_PERIOD}
                      yLabel={COPY.CHART_AXIS_CERTIFICATES}
                      y1Label={COPY.CHART_AXIS_REST_DAYS}
                    />
                  </ReportChartCard>
                </ReportsChartCol>
              ) : null}
            </ReportsChartGrid>
          )}

          {hasIssuedData && (
            <ReportsChartGrid>
              <ReportsChartCol lg={6}>
                <ReportChartCard
                  title={COPY.CHART_REST_DURATION_TITLE}
                  subtitle={COPY.CHART_REST_DURATION_DESC}
                  height={280}
                >
                  <ClinicalBarChart
                    data={durationBar}
                    yLabel={COPY.CHART_AXIS_CERTIFICATES}
                    xLabel={COPY.COL_REST_DURATION}
                  />
                </ReportChartCard>
              </ReportsChartCol>
              <ReportsChartCol lg={6}>
                <ReportChartCard
                  title={COPY.CHART_REST_DOCTORS_TITLE}
                  subtitle={COPY.CHART_REST_DOCTORS_DESC}
                  height={280}
                >
                  <ClinicalBarChart
                    data={doctorsBar}
                    yLabel={COPY.CHART_AXIS_REST_DAYS}
                    xLabel={COPY.COL_DOCTOR}
                  />
                </ReportChartCard>
              </ReportsChartCol>
            </ReportsChartGrid>
          )}
        </>
      )}

      {doctors.length > 0 && (
        <>
          <h3 className="hd-reports-table-title">{COPY.TABLE_REST_DOCTORS_TITLE}</h3>
          <StatisticalDataTable
            columns={tableColumns}
            rows={doctors}
            rowKey="doctor_id"
            maxValueKey="total_rest_days"
            loading={loading}
            emptyLabel={COPY.EMPTY_REST_DAYS}
            loadingLabel={COPY.LOADING}
          />
        </>
      )}
    </>
  );
}
